import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subscription, catchError, filter, of, switchMap, tap, timer } from 'rxjs';
import config from '../../../../../config/config';
import { Notificacion, RespuestaNotificaciones } from '../model/notificacion.model';
import { secureStorage } from '../../../core/data-access/services/localstorage.service';

const INTERVALO_POLLING_MS = 45000;
const LIMITE_POR_DEFECTO = 20;

const RESPUESTA_SIN_SESION: RespuestaNotificaciones = {
  status: false,
  message: 'Usuario no autenticado',
  body: null,
};

@Injectable({
  providedIn: 'root',
})
export class NotificacionesService implements OnDestroy {
  private readonly URL_BASE: string = config.URL_API_ISO45001 + '/notificaciones';

  private readonly notificacionesSubject = new BehaviorSubject<Notificacion[]>([]);
  public readonly notificaciones$ = this.notificacionesSubject.asObservable();

  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  public readonly unreadCount$ = this.unreadCountSubject.asObservable();

  private pollingSub?: Subscription;
  //cuantos componentes pidieron el polling; permite montar la campana en varios lugares sin abrir
  //un timer por cada uno ni que el primero en destruirse apague el de los demas.
  private suscriptoresPolling = 0;

  private ultimoUnreadCount = 0;
  private esPrimeraCarga = true;

  //un unico AudioContext reutilizado: crear uno por notificacion agota el limite del navegador
  //(Chrome corta alrededor de 6 contextos por pestana) y el sonido deja de escucharse.
  private audioCtx?: AudioContext;

  constructor(private readonly http: HttpClient) {}

  ngOnDestroy(): void {
    this.detenerPolling(true);
    this.audioCtx?.close().catch(() => undefined);
  }

  // ==========================================
  // Ciclo de vida del polling
  // ==========================================

  /**
   * Arranca el sondeo periodico. Lo llama el componente de la campana al montarse, no el constructor:
   * el servicio es providedIn 'root', asi que arrancar en el constructor dejaba un timer sondeando para
   * siempre, tambien en /login y despues de cerrar sesion.
   */
  public iniciarPolling(): void {
    this.suscriptoresPolling++;
    if (this.pollingSub) return;

    this.pollingSub = timer(0, INTERVALO_POLLING_MS)
      .pipe(
        //no se consulta con la pestana en segundo plano ni sin sesion activa
        filter(() => this.pestanaVisible() && this.haySesion()),
        //switchMap cancela la peticion anterior si aun no respondio: con la red lenta el timer de 45s
        //llegaba a encimar peticiones y la ultima en resolver no era necesariamente la mas reciente.
        switchMap(() => this.solicitarNotificaciones(1, LIMITE_POR_DEFECTO, true)),
      )
      .subscribe();
  }

  /** Detiene el sondeo. `forzar` lo apaga ignorando el conteo de suscriptores (logout, destroy). */
  public detenerPolling(forzar = false): void {
    if (!forzar) {
      this.suscriptoresPolling = Math.max(0, this.suscriptoresPolling - 1);
      if (this.suscriptoresPolling > 0) return;
    } else {
      this.suscriptoresPolling = 0;
    }

    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
  }

  /** Limpia el estado en memoria y apaga el polling. Debe llamarse al cerrar sesion. */
  public limpiar(): void {
    this.detenerPolling(true);
    this.notificacionesSubject.next([]);
    this.unreadCountSubject.next(0);
    this.ultimoUnreadCount = 0;
    this.esPrimeraCarga = true;
  }

  private pestanaVisible(): boolean {
    return typeof document === 'undefined' || !document.hidden;
  }

  private haySesion(): boolean {
    const { usuarioId, cedula } = this.obtenerDatosUsuarioLogueado();
    return !!usuarioId || !!cedula;
  }

  // ==========================================
  // Identidad
  // ==========================================

  public obtenerDatosUsuarioLogueado(): { usuarioId: number | null; cedula: string | null } {
    const user = secureStorage.getItem('userData');
    if (!user) return { usuarioId: null, cedula: null };

    //OJO: no se usa `sistema_id` como respaldo. sistema_id guarda int_usuario_sistema_centro_id, que es
    //otra secuencia distinta de int_usuario_id; como el backend filtra con un OR sobre int_usuario_id,
    //mandar el id equivocado podia devolver el buzon de OTRA persona con el mismo numero.
    const usuarioId = user.usuario?.int_usuario_id ?? user.int_usuario_id ?? null;

    const cedulaCruda =
      user.usuario?.str_usuario_cedula ??
      user.usuario?.str_usuario_identificacion ??
      user.str_usuario_cedula ??
      user.str_cedula ??
      user.cedula ??
      null;

    return {
      usuarioId: Number.isFinite(Number(usuarioId)) && Number(usuarioId) > 0 ? Number(usuarioId) : null,
      //normalizada, porque el backend guarda y compara la cedula con trim()
      cedula: cedulaCruda ? String(cedulaCruda).trim() || null : null,
    };
  }

  public obtenerUsuarioId(): number | null {
    return this.obtenerDatosUsuarioLogueado().usuarioId;
  }

  // ==========================================
  // Sonido
  // ==========================================

  public reproducirSonidoNotificacion(): void {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;

      this.audioCtx ??= new AudioCtx();
      const ctx = this.audioCtx;
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => undefined);
      }

      const now = ctx.currentTime;

      // Nota 1: D5 (587.33 Hz)
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(587.33, now);
      gain1.gain.setValueAtTime(0.12, now);
      gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

      // Nota 2: A5 (880 Hz) - Brillo neón suave
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(880, now + 0.08);
      gain2.gain.setValueAtTime(0.18, now + 0.08);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);

      osc1.start(now);
      osc1.stop(now + 0.25);
      osc2.start(now + 0.08);
      osc2.stop(now + 0.4);

      //los nodos quedan huerfanos si no se desconectan; con un poll cada 45s se acumulan
      osc1.onended = () => {
        osc1.disconnect();
        gain1.disconnect();
      };
      osc2.onended = () => {
        osc2.disconnect();
        gain2.disconnect();
      };
    } catch {
      // Ignorar bloqueos de autoplay del navegador
    }
  }

  // ==========================================
  // Consultas
  // ==========================================

  public cargarNotificaciones(page: number = 1, limit: number = LIMITE_POR_DEFECTO): Observable<RespuestaNotificaciones> {
    return this.solicitarNotificaciones(page, limit, false);
  }

  private solicitarNotificaciones(page: number, limit: number, esPolling: boolean): Observable<RespuestaNotificaciones> {
    const { usuarioId, cedula } = this.obtenerDatosUsuarioLogueado();
    if (!usuarioId && !cedula) {
      this.notificacionesSubject.next([]);
      this.unreadCountSubject.next(0);
      return of(RESPUESTA_SIN_SESION);
    }

    let params = new HttpParams().set('page', page).set('limit', limit);
    if (cedula) params = params.set('cedula', cedula);

    //el sondeo de fondo no debe expulsar al usuario: sin esta cabecera, un 401 del poll disparaba el
    //authInterceptor (clearLocalStorage + redirect a /login) en medio de un formulario a medio llenar.
    //El token vencido se detecta igual en la siguiente accion real del usuario.
    const headers = esPolling ? new HttpHeaders({ 'X-Skip-Auth-Redirect': 'true' }) : undefined;

    return this.http.get<RespuestaNotificaciones>(`${this.URL_BASE}/usuario/${usuarioId ?? 0}`, { params, headers }).pipe(
      tap((res) => this.aplicarRespuesta(res)),
      catchError((err) => {
        //si la sesion caduco no tiene sentido seguir sondeando cada 45s contra un 401
        if (esPolling && err?.status === 401) {
          this.detenerPolling(true);
        }
        return of({
          status: false,
          message: 'No se pudo conectar al servidor de notificaciones',
          body: null,
        } as RespuestaNotificaciones);
      }),
    );
  }

  private aplicarRespuesta(res: RespuestaNotificaciones): void {
    if (!res?.status || !res.body) return;

    const newCount = res.body.unreadCount || 0;
    this.notificacionesSubject.next(res.body.notificaciones || []);
    this.unreadCountSubject.next(newCount);

    // Sonido solo cuando llegan nuevas, nunca en la primera carga de la sesión
    if (newCount > this.ultimoUnreadCount && !this.esPrimeraCarga) {
      this.reproducirSonidoNotificacion();
    }
    this.ultimoUnreadCount = newCount;
    this.esPrimeraCarga = false;
  }

  // ==========================================
  // Mutaciones
  // ==========================================

  public marcarComoLeida(notificacionId: number): Observable<unknown> {
    const { usuarioId, cedula } = this.obtenerDatosUsuarioLogueado();
    if (!usuarioId && !cedula) return EMPTY;

    //la cedula viaja tambien aca: el backend verifica la pertenencia de la notificacion antes de marcarla
    let params = new HttpParams();
    if (cedula) params = params.set('cedula', cedula);

    return this.http.patch(`${this.URL_BASE}/${notificacionId}/usuario/${usuarioId ?? 0}/leer`, {}, { params }).pipe(
      tap(() => {
        const actuales = this.notificacionesSubject.value;
        const objetivo = actuales.find((n) => n.int_notificacion_id === notificacionId);
        //solo se descuenta si de verdad estaba sin leer; antes restaba 1 en cada clic y el contador
        //se desincronizaba del servidor
        if (!objetivo || objetivo.bool_leido) return;

        this.notificacionesSubject.next(
          actuales.map((n) =>
            n.int_notificacion_id === notificacionId ? { ...n, bool_leido: true, dt_leido_en: new Date().toISOString() } : n,
          ),
        );

        const unread = Math.max(0, this.unreadCountSubject.value - 1);
        this.unreadCountSubject.next(unread);
        this.ultimoUnreadCount = unread;
      }),
    );
  }

  public marcarTodasComoLeidas(): Observable<unknown> {
    const { usuarioId, cedula } = this.obtenerDatosUsuarioLogueado();
    if (!usuarioId && !cedula) return EMPTY;

    let params = new HttpParams();
    if (cedula) params = params.set('cedula', cedula);

    return this.http.patch(`${this.URL_BASE}/usuario/${usuarioId ?? 0}/leer-todas`, {}, { params }).pipe(
      tap(() => {
        this.notificacionesSubject.next(
          this.notificacionesSubject.value.map((n) => ({
            ...n,
            bool_leido: true,
            dt_leido_en: n.dt_leido_en ?? new Date().toISOString(),
          })),
        );
        this.unreadCountSubject.next(0);
        this.ultimoUnreadCount = 0;
      }),
    );
  }
}
